import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  adminGetPartnerInvoice,
  adminCreatePartnerCreditNote,
} from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";
import ConfirmModal from "./confirm-modal";

const fmtEUR = (n) =>
  `€ ${Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");

const PartnerInvoiceDetailModal = ({
  isOpen,
  invoiceId,
  onClose,
  onChanged,
}) => {
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [selectedLines, setSelectedLines] = useState(new Set());
  const [creditReason, setCreditReason] = useState("");
  const [confirmCreditOpen, setConfirmCreditOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen || !invoiceId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminGetPartnerInvoice(invoiceId)
      .then((res) => {
        if (cancelled) return;
        const inv = res?.data ?? res;
        setInvoice(inv);
        setSelectedLines(new Set());
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.data?.error ?? e?.message ?? "Kon factuur niet laden.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, invoiceId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, submitting, onClose]);

  const toggleLine = (appId) => {
    setSelectedLines((prev) => {
      const next = new Set(prev);
      const key = String(appId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const runCreditNote = async () => {
    if (!invoice) return;
    const applicationIds =
      selectedLines.size > 0 ? Array.from(selectedLines) : undefined;
    setSubmitting(true);
    try {
      await adminCreatePartnerCreditNote(invoice._id, {
        applicationIds,
        reason: creditReason || undefined,
      });
      setConfirmCreditOpen(false);
      setCreditReason("");
      onChanged?.();
      onClose();
    } catch (e) {
      alert(e?.response?.data?.error ?? "Creditnota aanmaken mislukt.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const isCredit = invoice?.type === "credit_note";
  const canCredit =
    invoice &&
    !isCredit &&
    invoice.status !== "draft" &&
    invoice.status !== "cancelled";

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-3xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-6">
          <div>
            <h2 className="font-display text-xl text-gray-900">
              {invoice?.invoiceNumber ?? "Factuur"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {invoice?.billingName} &middot;{" "}
              {fmtDate(invoice?.invoiceDate)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100"
            aria-label="Sluiten"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Laden…</div>
          ) : error ? (
            <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
              {error}
            </div>
          ) : invoice ? (
            <>
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <span className="text-gray-500">Kenmerk:</span>{" "}
                  <span className="font-mono">{invoice.kenmerk}</span>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>{" "}
                  {invoice.status}
                </div>
                <div>
                  <span className="text-gray-500">Email:</span>{" "}
                  {invoice.billingEmail}
                </div>
                {invoice.dueDate ? (
                  <div>
                    <span className="text-gray-500">Vervaldatum:</span>{" "}
                    {fmtDate(invoice.dueDate)}
                  </div>
                ) : null}
                {invoice.sentAt ? (
                  <div>
                    <span className="text-gray-500">Verstuurd op:</span>{" "}
                    {fmtDate(invoice.sentAt)}
                  </div>
                ) : null}
                {invoice.paidAt ? (
                  <div>
                    <span className="text-gray-500">Betaald op:</span>{" "}
                    {fmtDate(invoice.paidAt)}
                  </div>
                ) : null}
                {invoice.creditedAmount ? (
                  <div className="text-rose-700">
                    <span className="text-gray-500">Gecrediteerd:</span>{" "}
                    {fmtEUR(invoice.creditedAmount)}
                  </div>
                ) : null}
              </div>

              {/* Regels */}
              <div className="mt-6">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Regels ({invoice.lines?.length ?? 0})
                </h3>
                <div className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {(invoice.lines ?? []).map((line) => {
                    const key = String(line.btwApplicationId);
                    const checked = selectedLines.has(key);
                    return (
                      <label
                        key={key}
                        className={`flex items-center gap-3 p-3 ${
                          canCredit ? "cursor-pointer hover:bg-gray-50" : ""
                        }`}
                      >
                        {canCredit ? (
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleLine(key)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        ) : null}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">
                            {line.customerName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {line.description} &middot;{" "}
                            {fmtDate(line.applicationDate)}
                          </p>
                        </div>
                        <div className="text-right tabular-nums">
                          <div className="text-sm font-medium">
                            {fmtEUR(line.amountIncl)}
                          </div>
                          <div className="text-xs text-gray-400">
                            excl. {fmtEUR(line.amountExcl)}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Totals */}
              <div className="mt-4 flex justify-end">
                <div className="w-72 rounded-lg bg-gray-50 p-3 text-sm">
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-500">Subtotaal (excl.)</span>
                    <span>{fmtEUR(invoice.subtotalExcl)}</span>
                  </div>
                  <div className="flex justify-between py-0.5">
                    <span className="text-gray-500">BTW</span>
                    <span>{fmtEUR(invoice.vatAmount)}</span>
                  </div>
                  <div className="mt-1 flex justify-between border-t border-gray-200 pt-1 font-semibold text-gray-900">
                    <span>Totaal (incl.)</span>
                    <span>{fmtEUR(invoice.totalIncl)}</span>
                  </div>
                </div>
              </div>

              {invoice.notes ? (
                <p className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-600">
                  {invoice.notes}
                </p>
              ) : null}

              {canCredit ? (
                <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 p-4">
                  <h4 className="text-sm font-semibold text-rose-900">
                    Creditnota maken
                  </h4>
                  <p className="mt-1 text-xs text-rose-800">
                    {selectedLines.size === 0
                      ? "Geen regels geselecteerd: hele factuur wordt gecrediteerd."
                      : `${selectedLines.size} regel(s) geselecteerd voor creditering.`}
                  </p>
                  <textarea
                    rows={2}
                    value={creditReason}
                    onChange={(e) => setCreditReason(e.target.value)}
                    placeholder="Reden (optioneel)"
                    className="mt-2 w-full rounded-md border border-rose-300 bg-white p-2 text-sm focus:border-rose-500 focus:outline-none"
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setConfirmCreditOpen(true)}
                      disabled={submitting}
                      className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                    >
                      Maak creditnota
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Sluiten
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmCreditOpen}
        variant="danger"
        title="Creditnota aanmaken?"
        message={
          selectedLines.size === 0
            ? "De gehele factuur wordt gecrediteerd en de partner ontvangt een creditnota per mail."
            : `${selectedLines.size} regel(s) worden gecrediteerd. De partner ontvangt een creditnota per mail.`
        }
        confirmLabel="Maak creditnota"
        cancelLabel="Annuleren"
        busy={submitting}
        onConfirm={runCreditNote}
        onCancel={() => setConfirmCreditOpen(false)}
      />
    </div>,
    document.body,
  );
};

export default PartnerInvoiceDetailModal;
